import React, { forwardRef } from 'react';
import { LinkProps, NavLink } from 'react-router-dom';
import { Button, IButtonProps } from '../button/Button';

interface IBaseButtonLinkProps extends Omit<IButtonProps, 'onClick'> {
    target?: LinkProps['target'];
    rel?: LinkProps['rel'];
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

// props provided to the NavLink component
type IInternalButtonLinkProps = IBaseButtonLinkProps & {
    to: LinkProps['to'];
    exact?: boolean;
} & { external?: never; href?: never };

// props provided to the anchor tag
type IExternalButtonLinkProps = IBaseButtonLinkProps & {
    href: string;
} & { external: true; to?: never; exact?: never };

export type IButtonLinkProps = IInternalButtonLinkProps | IExternalButtonLinkProps;

/**
 * Wrapper for the Button component that allows it to be used as a link.
 *
 * ---
 *
 * ## Linking to an Internal Page
 *
 * If the target URL is an internal page, the ButtonLink component will
 * render a NavLink component from react-router-dom.
 *
 * To specify the target URL is an internal page the `external` prop
 * must be omitted.
 *
 * ---
 *
 * ## Linking to an External Page
 *
 * If the target URL is an external page, the ButtonLink component will
 * render an anchor tag.
 *
 * To specify the target URL is an external page the `external` prop
 * must be set to `true`.
 *
 * ### Security
 *
 * When using `target="_blank"` it is [recommended](https://developer.chrome.com/docs/lighthouse/best-practices/external-anchors-use-rel-noopener/)
 * to always set `rel="noopener noreferrer"`.
 *
 * - `rel="noopener"` prevents the new page from being able to access the
 *  `window.opener` property and ensures it runs in a separate process.
 * - `rel="noreferrer"` prevents the browser from sending the _Referer_
 * header to the new page.
 *
 */
export const ButtonLink = forwardRef<HTMLElement, IButtonLinkProps>(function ButtonLink(
    props,
    ref
) {
    const { external, href, to, target, rel, exact, onClick, children, ...button_props } = props;

    /**
     * Wrapper for the onClick prop. Conditionally calls the onClick prop
     * if it is provided. Using the onClick prop should be avoided as the
     * primary function of the ButtonLink is to navigate to a new page.
     * This onClick handler is primarily  provided for backwards compatibility
     * with older pages.
     * @param event - the click event
     */
    function onClickHandler(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        if (onClick) {
            onClick(event);
        }
    }

    const button = (
        <Button {...button_props} onClick={onClickHandler}>
            {children}
        </Button>
    );

    if (external) {
        const link_props = { href, target, rel };

        return (
            <a ref={ref as React.RefObject<HTMLAnchorElement>} {...link_props}>
                {button}
            </a>
        );
    } else {
        const link_props = { to, target, rel, exact };

        return (
            <NavLink ref={ref as React.RefObject<HTMLAnchorElement>} {...link_props}>
                {button}
            </NavLink>
        );
    }
});
